import React from 'react';
import { ISerializableError, PrivateDataRequested, Requested, TickData } from '../types/api/Types';
import { isPrivateDataError } from '../types/api/PrivateTypes';
import { IPublicTradeForGroup, PublicOrderTickData } from '../types/api/PublicTypes';
import { GET_SIGTI_API_URL } from '../config';
import { uploadFileMutation } from '../redux-store/queries/alertQueries';
import { IAlertReviewAttachment } from '../types/api/AlertTypes';

/** ENVIRONMENT */
export const isEnvironmentProduction = () => (process.env.REACT_APP_NODE_ENV === 'production');


/* DATES */
export const turnDateToSimpleTime = (day: Date) => {
    return day.getFullYear().toString().padStart(4, '0') +
    '-' +
    (day.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    day.getDate().toString().padStart(2, '0');
}

export const getNextSimpleDate = (simpleDateString: string): string => {
    const currentTime = new Date(simpleDateString);
    const oneDayFromNow = new Date(currentTime.getTime() + (24*60*60*1000));
    return turnDateToSimpleTime(oneDayFromNow);
}

export const getLastTradingDay = () => {
    const getPreviousDay = (day: Date) => {
        const oneDayInMilliseconds = 24*60*60*1000;
        return new Date(day.getTime() - oneDayInMilliseconds);
    }
    const today = new Date();
    const yesterday = getPreviousDay(today);
    let lastTradingDay = yesterday;
    const WEEKEND = [0,6] // Sunday is 0, Saturday is 6. (Mon-Fri is 1-5)
    while (WEEKEND.includes(lastTradingDay.getDay())) {
        lastTradingDay = getPreviousDay(lastTradingDay);
    }
    const simpleDateTradingDay = turnDateToSimpleTime(lastTradingDay);
    return simpleDateTradingDay;
}

export const getDatePair = (fromDate: string, toDate: string) => {
    return fromDate + '-' + toDate;
}


/**
 * For loop for simple dates.
 */
export const _for = (fromDate: string, toDate: string, callback: (currentDate: string) => void | 'break') => {
    // Prevent endless loop if fromDate is already ahead of toDate.
    if (new Date(fromDate) > new Date(toDate)) return;
    
    const dayAfterToDate = getNextSimpleDate(toDate)
    for (let currentDate = fromDate; currentDate !== dayAfterToDate; currentDate = getNextSimpleDate(currentDate)) {
        const functionReturnValue = callback(currentDate);
        if (functionReturnValue === 'break') {
            break;
        }
    }
}


/* FORMATTING */
export function formatDate(date: string | null | undefined, onlyTime?: boolean, showMs?: boolean): string {
    if (date === null || date === undefined) {
        return '-';
    }
    const dateObject = new Date(date);
    const formattedDateString = (onlyTime)
        ? dateObject.toLocaleTimeString('de-DE', { timeZone: 'UTC' }).replace(',', ' ')
        : dateObject.toLocaleString('de-DE', { timeZone: 'UTC' }).replace(',', ' ');
    if (showMs) {
        return formattedDateString + '.' + dateObject.getMilliseconds().toString().padStart(3, '0');
    }
    return formattedDateString;
}

// 2025-03-21 00:00:00.000 -> 21.3.2025
export function formatDay(date: string | null | undefined): string {
    if (date === null || date === undefined) {
        return '-';
    }
    const dateObject = new Date(date);
    return dateObject.toLocaleDateString('de-DE', { timeZone: 'UTC' }).replace(',', ' ');
}
// 2025-03-21 00:00:00.000 -> 2025-03-21
export function formatDayV2(date: string | null | undefined): string {
    if (!date) {
      return "";
    }
    const parts = date.split(" ");
    return parts[0] || "";
}

export function formatTime(time: string | null | undefined, showMs?: boolean): string {
    if (time === null || time === undefined) {
        return '-';
    }
    const timeObject = new Date(time);
    const formattedTimeString = timeObject.toLocaleTimeString('de-DE', { timeZone: 'UTC' }).replace(',', ' ');
    if (showMs) {
        return formattedTimeString + '.' + timeObject.getMilliseconds().toString().padStart(3, '0');
    }
    return formattedTimeString;
}

type NumberTypes = 'volume' | 'shortened'
export function formatNumber(number: number | undefined | null, numberType?: NumberTypes): string | JSX.Element {
    if (number === null || number === undefined) {
        return '-';
    }
    const fractionDigits =
        (numberType === 'volume') // if
        ? 0
        : (numberType === 'shortened') // else if
        ? 2
        // Default.
        : 3;
    const formattedNumber = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    }).format(number);
    const DECIMAL = ',';

    const splitOnDecimal = formattedNumber.split(DECIMAL);
    const numbersBeforeDecimal = splitOnDecimal[0];
    const numbersAfterDecimal = splitOnDecimal[1] || null;
    // If it has decimal afterwards, add span for them.
    if (numbersAfterDecimal !== null) {
        return (
            <>
                {numbersBeforeDecimal}
                {DECIMAL}
                <span className='DecimalSpan'>
                    {numbersAfterDecimal}
                </span>
            </>
        );
    }

    return formattedNumber;
}


/* HISTORY API */
export const pushQueryToHistory = (key: string, value: string | null, shouldReplace?: boolean) => {
    const currentQueryString = window.location.search.slice(1);
    const urlParams = new URLSearchParams(currentQueryString);
    if (value === null) {
        urlParams.delete(key);
    }
    else if (currentQueryString.includes(key)) {
        urlParams.set(key, value);
    } else {
        urlParams.append(key, value);
    }
    const currentUrlWithoutQuery = window.location.href.split('?')[0];
    const newUrl = currentUrlWithoutQuery + `?${urlParams.toString()}`;
    if (shouldReplace) {
        window.history.replaceState(null, newUrl, newUrl);
    } else {
        window.history.pushState(null, newUrl, newUrl);
    }
}

// INSERT AND DELETE OPERATIONS FOR BOOK-CALCULATION
export const insertOrderForBook = (newOrderHistoryEntry: PublicOrderTickData, bookArray: PublicOrderTickData[], isSideBid: boolean) => {
    // Insert into priority array.
    let insertOrderIndex = bookArray.findIndex(order => {
        if (order.price === null) return false;
        if (newOrderHistoryEntry.price === null) return false;
        if (isSideBid) {
            return (order.price <= newOrderHistoryEntry.price);
        } else {
            return (order.price >= newOrderHistoryEntry.price);
        }
    });
    // No order with smaller price found. Insert at end of array.
    if (insertOrderIndex === -1) {
        bookArray.push(newOrderHistoryEntry);
    }
    // New order must be inserted at index of order closest to it. All other orders are shifted.
    else {
        // The orders of same price must be sorted by time to simulate a real orderbook.
        // Orders have priority depending on when they entered the book.
        let insertOrderIndexAfterTimeOffset = insertOrderIndex;
        // While the order at the insert index has a smaller (earlier) time than the new order, the new order has to go deeper into the array.
        while (
            bookArray[insertOrderIndexAfterTimeOffset].price === newOrderHistoryEntry.price
            && (
                new Date(bookArray[insertOrderIndexAfterTimeOffset].order_timestamp_utc) < new Date(newOrderHistoryEntry.order_timestamp_utc)
                || (
                    new Date(bookArray[insertOrderIndexAfterTimeOffset].order_timestamp_utc).toISOString() === new Date(newOrderHistoryEntry.order_timestamp_utc).toISOString()
                    && (bookArray[insertOrderIndexAfterTimeOffset].order_id || '') < (newOrderHistoryEntry.order_id || '')
                )
            )
        ) {
            insertOrderIndexAfterTimeOffset++;
            // If insertion index has arrived at the end of the array, break out.
            if (bookArray[insertOrderIndexAfterTimeOffset] === undefined) break;
        }
        // Insert new order history entry at the index.
        bookArray.splice(insertOrderIndexAfterTimeOffset, 0, newOrderHistoryEntry);
    }
    return bookArray;
}

export const deleteOrderFromBook = (deleteThisOrderHistoryEntry: PublicOrderTickData, bookArray: PublicOrderTickData[]) => {
    return bookArray.filter(orderHistoryEntry => (
        orderHistoryEntry.order_id !== deleteThisOrderHistoryEntry.order_id
    ));
}


/* DATA MANIPULATION */

export const turnRequestedToData = <T extends any, U extends any>(requestedData: Requested<T> | PrivateDataRequested<T>, fallBack: U): T | U => {
    if (requestedData === undefined || requestedData === 'waiting' || isReqError(requestedData) || isPrivateDataError(requestedData)) {
        return fallBack;
    }
    return requestedData;
}

export function createPlaceholderComponents(component: JSX.Element, amount: number) {
    if (amount <= 0) {
        return null;
    }
    return Array.from({ length: amount }).map((_, index) => React.cloneElement(component, {
        key: index
    }));
}

export const getUniqueTickId = (tick: TickData) => {
    switch (tick.tickdata_type) {
        case 'Order':
            return `Order-${tick.order_id}-${tick.timestamp}`;
        case 'Order By Level':
            return `OrderByLevel-${tick.id}`;
        case 'Trade':
            return `Trade-${tick.trade_number}-${tick.timestamp}`;
        case 'PrivateOrder':
            return `PrivateOrder-${tick.market_feed_id}-${tick.timestamp}`;
        case 'PrivateTrade':
            return `PrivateTrade-${tick.trade_id}-${tick.order_id}-${tick.timestamp}`;
        case 'PrivateTradeReport':
            return `PrivateTradeReport-${tick.id}-${tick.trade_id}-${tick.timestamp}`;
        case 'Alert':
            return `Alert-${tick.ad_id}`;
        case 'Bookmark':
            return `Bookmark-${tick.id}`;
        case 'News':
            return `News-${tick.id}`;
        default:
            return null;
    }
}

export const getTickTimestamp = (tick: TickData) => {
    switch (tick.tickdata_type) {
        case 'Order':
            return tick.timestamp + "Z";
        case 'Order By Level':
            return tick.timestamp + "Z";
        case 'Trade':
            return tick.timestamp + "Z";
        case 'PrivateOrder':
            return tick.timestamp;
        case 'PrivateTrade':
            return tick.timestamp;
        case 'PrivateTradeReport':
            return tick.timestamp;
        case 'Alert':
            return tick.ref_timestamp;
        case 'Bookmark':
            return tick.timestamp;
        case 'News':
            return tick.timestamp + "Z";
        default:
            return null;
    }
}

export const getUniqueTickIdForPublicTradeForGroup = (publicTradeForGroup: IPublicTradeForGroup) => {
    const { trade_number, timestamp } = publicTradeForGroup;
    return `Trade-${trade_number}-${timestamp}`;
}

export function isReqError(payload: any): payload is ISerializableError {
    return typeof payload?.message === 'string';
}

export async function uploadFileToS3(file: File, reviewId: number, groupId: string, accessToken?: string): Promise<IAlertReviewAttachment | null> {
    const formData = new FormData();
    formData.append('operations', JSON.stringify({
        query: uploadFileMutation(),
        variables: { file: null, reviewId, groupId }
    }));
    formData.append('map', JSON.stringify({ '0': ['variables.file'] }));
    formData.append('0', file);

    const response = await fetch(GET_SIGTI_API_URL(), {
        method: 'POST',
        headers: {
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
            'x-apollo-operation-name': 'uploadFileMutation'
        },
        body: formData
    });
    if (!response.ok) {
        console.error('[uploadFileToS3] Upload failed:', await response.text());
        return null;
    }
    const result = await response.json();
    const data = result?.data?.addAlertReviewAttachment;
    if (!data) {
        console.error('[uploadFileToS3] No data returned from backend. Full result:', result);
        return null;
    }

    return {
        alert_review_id: data.alert_review_id,
        s3_key: data.s3_key,
        filename: data.filename,
        content_type: data.content_type,
        file_size_bytes: data.file_size_bytes,
        uploaded_timestamp: data.uploaded_timestamp,
    };
}

export async function uploadAllAttachments(files: File[], reviewId: number, groupId: string, accessToken?: string): Promise<IAlertReviewAttachment[]> {
  const uploaded: IAlertReviewAttachment[] = [];
  for (const file of files) {
    const meta = await uploadFileToS3(file, reviewId, groupId, accessToken);
    if (meta) {
      uploaded.push(meta);
    } else {
      console.error('[uploadAllAttachments] Upload failed for file:', file.name);
    }
  }
  return uploaded;
}
